import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle } from '../theme';

const SEGMENT_META = {
  vip:     { label:'💎 VIP',     color:'#9333ea', bg:'#f3e8ff', desc:'High spend (₹1L+), active in last 60 days' },
  loyal:   { label:'⭐ Loyal',    color:'#0891b2', bg:'#cffafe', desc:'5+ visits, active in last 90 days' },
  regular: { label:'👥 Regular',  color:'#16a34a', bg:'#dcfce7', desc:'Normal cadence of visits' },
  new:     { label:'🆕 New',      color:'#2563eb', bg:'#dbeafe', desc:'First visit within last 30 days' },
  dormant: { label:'💤 Dormant',  color:'#dc2626', bg:'#fee2e2', desc:'No visit in 180+ days — churn risk' },
};

const PatientLTVPage = ({ API_BASE }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [segFilter, setSegFilter] = useState('all');
  const [limit, setLimit] = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/analytics-engine/patient-ltv`, { params: { limit } });
      setData(r.data);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, limit]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ padding:40, textAlign:'center', color:'#888' }}>Crunching patient data…</div>;
  if (!data) return null;

  const rows = segFilter === 'all' ? data.top : data.top.filter(r => r.segment === segFilter);

  const stat = (label, val, color) => (
    <div style={{ ...cardStyle, textAlign:'center', minWidth:160 }}>
      <div style={{ fontSize:'0.72rem', color:theme.textMuted, fontWeight:600, textTransform:'uppercase' }}>{label}</div>
      <div style={{ fontSize:'1.6rem', fontWeight:700, color: color || theme.navy, marginTop:4 }}>{val}</div>
    </div>
  );

  const segCount = (k) => data.segments[k] || 0;
  const totalPts = data.totals.patients || 1;

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 12px' }}>📊 Patient Lifetime Value & Segmentation</h2>
      <div style={{ ...cardStyle, marginBottom:12, background:theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color:theme.navy, lineHeight:1.5 }}>
          <b>LTV-based patient segments:</b> identify who drives revenue (VIP/Loyal), who needs winback (Dormant), who's new and worth nurturing. Use segments for targeted campaigns — VIP → premium offers, Dormant → WhatsApp winback, Loyal → referral asks.
        </div>
      </div>

      {/* Headline stats */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        {stat('Total Patients', data.totals.patients || 0)}
        {stat('Total Lifetime Value', `₹${(data.totals.totalValue || 0).toLocaleString('en-IN')}`, theme.success)}
        {stat('Average LTV', `₹${(data.totals.avgLtv || 0).toLocaleString('en-IN')}`)}
      </div>

      {/* Segment cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:10, marginBottom:16 }}>
        {Object.entries(SEGMENT_META).map(([k, meta]) => {
          const count = segCount(k);
          const pct = Math.round((count / totalPts) * 100);
          const active = segFilter === k;
          return (
            <div key={k} onClick={()=>setSegFilter(active ? 'all' : k)}
              style={{ ...cardStyle, padding:14, cursor:'pointer',
                borderLeft:`4px solid ${meta.color}`,
                outline: active ? `2px solid ${theme.primary}` : 'none',
                background: active ? theme.primaryLight : 'white' }}>
              <div style={{ fontSize:'0.9rem', fontWeight:700, color: meta.color }}>{meta.label}</div>
              <div style={{ fontSize:'1.6rem', fontWeight:800, color: theme.navy, marginTop:6 }}>{count}</div>
              <div style={{ fontSize:'0.7rem', color:'#666', marginTop:2 }}>{pct}% of patients</div>
              <div style={{ fontSize:'0.7rem', color:'#888', marginTop:6, lineHeight:1.3 }}>{meta.desc}</div>
            </div>
          );
        })}
      </div>

      {/* Filter bar */}
      <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
        <button onClick={()=>setSegFilter('all')}
          style={{ padding:'6px 14px', fontSize:'0.8rem', borderRadius:6, border:'1px solid #e5e7eb', cursor:'pointer',
            background: segFilter==='all' ? theme.primary : 'white', color: segFilter==='all' ? 'white' : '#333', fontWeight:600 }}>
          All ({data.top.length})
        </button>
        {Object.entries(SEGMENT_META).map(([k, meta]) => (
          <button key={k} onClick={()=>setSegFilter(k)}
            style={{ padding:'6px 14px', fontSize:'0.8rem', borderRadius:6, border:'1px solid #e5e7eb', cursor:'pointer',
              background: segFilter===k ? meta.color : 'white', color: segFilter===k ? 'white' : '#333', fontWeight:600 }}>
            {meta.label} ({segCount(k)})
          </button>
        ))}
        <div style={{ marginLeft:'auto', display:'flex', gap:6, alignItems:'center' }}>
          <span style={{ fontSize:'0.8rem', color:'#666' }}>Show top:</span>
          {[50, 100, 200, 500].map(n => (
            <button key={n} onClick={()=>setLimit(n)}
              style={{ padding:'4px 10px', fontSize:'0.75rem', borderRadius:6, border:'1px solid #e5e7eb', cursor:'pointer',
                background: limit===n ? theme.navy : 'white', color: limit===n ? 'white' : '#333', fontWeight:600 }}>
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {rows.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No patients in this segment.</div> :
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'#f9fafb' }}>
            <th style={th}>#</th>
            <th style={th}>Patient</th>
            <th style={th}>Segment</th>
            <th style={th}>Total Spend</th>
            <th style={th}>Visits</th>
            <th style={th}>Avg/Visit</th>
            <th style={th}>Last Visit</th>
            <th style={th}>First Visit</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const meta = SEGMENT_META[r.segment] || SEGMENT_META.regular;
              return (
                <tr key={r.patientId} style={{ borderBottom:'1px solid #f1f3f5' }}>
                  <td style={{ ...td, color:'#888', fontSize:'0.78rem' }}>{i+1}</td>
                  <td style={{ ...td, fontWeight:600 }}>
                    {r.name}
                    <div style={{ fontSize:'0.72rem', color:'#666' }}>
                      {r.phone}{r.uhid && ` · ${r.uhid}`}
                    </div>
                  </td>
                  <td style={td}>
                    <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, background: meta.bg, color: meta.color }}>
                      {meta.label}
                    </span>
                  </td>
                  <td style={{ ...td, fontWeight:700, color: theme.success }}>₹{(r.totalSpend || 0).toLocaleString('en-IN')}</td>
                  <td style={td}>{r.visits}</td>
                  <td style={td}>₹{(r.avgSpend || 0).toLocaleString('en-IN')}</td>
                  <td style={{ ...td, color: r.recencyDays > 180 ? theme.danger : r.recencyDays > 90 ? theme.warning : '#333', fontWeight: r.recencyDays > 90 ? 600 : 400 }}>
                    {r.lastDate ? `${new Date(r.lastDate).toLocaleDateString('en-IN')}` : '—'}
                    <div style={{ fontSize:'0.7rem', color:'#888' }}>{r.recencyDays}d ago</div>
                  </td>
                  <td style={{ ...td, fontSize:'0.8rem', color:'#666' }}>
                    {r.firstDate ? new Date(r.firstDate).toLocaleDateString('en-IN') : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>}
      </div>
    </div>
  );
};

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };

export default PatientLTVPage;
